import type { Circuit, PlayerTrackPosition, TrackCell } from '../types/track.js';
import { getPlayerTrackCell, normalizeMainPathIndex } from './jokerLap.js';
import { getCorePath } from './circuitEdit.js';

export function physicalCellKey(cell: Pick<TrackCell, 'x' | 'y'>): string {
  return `${cell.x},${cell.y}`;
}

/** Case grille (x,y) — sur une boucle S/F, indices 0 et pathLen-1 partagent cette coordonnée. */
export function physicalGridKey(circuit: Circuit, position: PlayerTrackPosition): string | null {
  const cell = getPlayerTrackCell(circuit, position);
  return cell ? physicalCellKey(cell) : null;
}

function pathHasStartFinishOverlap(circuit: Circuit): boolean {
  const pathLen = circuit.path.length;
  if (pathLen < 2) return false;
  const start = circuit.path[0];
  const end = circuit.path[pathLen - 1];
  return start.x === end.x && start.y === end.y;
}

function isUnitCardinal(dx: number, dy: number): boolean {
  return Math.abs(dx) + Math.abs(dy) === 1;
}

/** Virage à 90° : la direction d’entrée et de sortie diffèrent. */
export function isRightAngleCorner(prev: TrackCell, curr: TrackCell, next: TrackCell): boolean {
  const inDx = curr.x - prev.x;
  const inDy = curr.y - prev.y;
  const outDx = next.x - curr.x;
  const outDy = next.y - curr.y;
  if (!isUnitCardinal(inDx, inDy) || !isUnitCardinal(outDx, outDy)) return false;
  return inDx !== outDx || inDy !== outDy;
}

interface PathSample {
  x: number;
  y: number;
  key: string;
  corner: boolean;
}

/**
 * Polyligne du tracé principal (sans la case de fermeture dupliquée), du joker
 * et de la ligne de départ, avec le voisinage réel pour détecter les virages 90°.
 */
function listPathSamples(circuit: Circuit): PathSample[] {
  const samples: PathSample[] = [];

  const pushPolyline = (cells: readonly TrackCell[], loop: boolean): void => {
    const n = cells.length;
    if (n === 0) return;
    for (let i = 0; i < n; i++) {
      const curr = cells[i];
      const prev = loop ? cells[(i - 1 + n) % n] : i > 0 ? cells[i - 1] : curr;
      const next = loop ? cells[(i + 1) % n] : i < n - 1 ? cells[i + 1] : curr;
      samples.push({
        x: curr.x,
        y: curr.y,
        key: physicalCellKey(curr),
        corner: isRightAngleCorner(prev, curr, next),
      });
    }
  };

  // Clusters de virages = tracé principal uniquement.
  // Le tour joker et la ligne de départ restent en clé grille par case : un
  // escalier 90° sur le joker fusionné en un seul bucket (capacité 2) bloque
  // définitivement 6 pilotes obligés d’y passer.
  const core = getCorePath(circuit.path);
  pushPolyline(core, core.length > 2);
  return samples;
}

const ORTHO = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

/** physicalKey → occupancyKey (cluster si ≥ 2 virages 90° adjacents). */
const occupancyByCircuit = new WeakMap<Circuit, Map<string, string>>();

function floodCluster(origin: string, corners: ReadonlySet<string>): string[] {
  const cluster: string[] = [];
  const seen = new Set<string>([origin]);
  const queue = [origin];
  while (queue.length > 0) {
    const key = queue.pop()!;
    cluster.push(key);
    const [xs, ys] = key.split(',');
    const x = Number(xs);
    const y = Number(ys);
    for (const [dx, dy] of ORTHO) {
      const nextKey = `${x + dx},${y + dy}`;
      if (corners.has(nextKey) && !seen.has(nextKey)) {
        seen.add(nextKey);
        queue.push(nextKey);
      }
    }
  }
  cluster.sort();
  return cluster;
}

function occupancyIndex(circuit: Circuit): Map<string, string> {
  const cached = occupancyByCircuit.get(circuit);
  if (cached) return cached;

  const samples = listPathSamples(circuit);
  const corners = new Set<string>();
  for (const sample of samples) {
    if (sample.corner) corners.add(sample.key);
  }

  const physicalToOcc = new Map<string, string>();
  const assigned = new Set<string>();
  for (const origin of corners) {
    if (assigned.has(origin)) continue;
    const cluster = floodCluster(origin, corners);
    const occKey = cluster.length >= 2 ? `cornerCluster:${cluster[0]}` : origin;
    for (const key of cluster) {
      physicalToOcc.set(key, occKey);
      assigned.add(key);
    }
  }

  occupancyByCircuit.set(circuit, physicalToOcc);
  return physicalToOcc;
}

/**
 * Identifiant d’un chapelet de virages 90° orthogonaux adjacents (escalier,
 * chicane). Sans ça, chaque case a une clé grille distincte alors que les
 * voitures se retrouvent visuellement sur le même virage — et le dépassement
 * n’est jamais tenté.
 *
 * Un virage 90° isolé n’est pas un cluster : la clé grille suffit.
 */
export function cornerClusterKey(circuit: Circuit, position: PlayerTrackPosition): string | null {
  const origin = physicalGridKey(circuit, position);
  if (!origin) return null;
  const occ = occupancyIndex(circuit).get(origin);
  if (!occ || !occ.startsWith('cornerCluster:')) return null;
  return occ;
}

/**
 * Sur une boucle S/F (même tuile monde pour index 0 et pathLen-1), l’occupation
 * traite départ et arrivée comme deux cases distinctes.
 */
function withStartFinishOccupancySide(
  circuit: Circuit,
  position: PlayerTrackPosition,
  baseKey: string
): string {
  if (!pathHasStartFinishOverlap(circuit)) return baseKey;
  if (position.jokerBranchIndex != null || position.startLaneIndex != null) return baseKey;
  const pathLen = circuit.path.length;
  const idx = normalizeMainPathIndex(position.mainPathIndex, pathLen);
  if (idx === 0) return `${baseKey}#sfStart`;
  if (idx === pathLen - 1) return `${baseKey}#sfFinish`;
  return baseKey;
}

/**
 * Clé d’occupation pour le déplacement : case physique, ou cluster de virages
 * 90° adjacents (une seule « case » de conflit — dépassement obligatoire).
 * Boucle S/F : départ (index 0) et arrivée (pathLen-1) restent deux clés distinctes.
 */
export function trackMovementOccupancyKey(
  circuit: Circuit,
  position: PlayerTrackPosition
): string | null {
  const physical = physicalGridKey(circuit, position);
  if (!physical) return null;
  const base = occupancyIndex(circuit).get(physical) ?? physical;
  return withStartFinishOccupancySide(circuit, position, base);
}
